import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ArrowLeft } from "lucide-react";
import { GLOBAL_IMAGES } from "../../../assets/asset";
import { getClassBookingBasePath } from "../../../features/parentFlow/parentFlow";
import { logout } from "../../../features/auth/user.reducer";
import LogoutConfirmDialog from "../../LogoutConfirmDialog";

const Header = ({
  heading,
  subheading,
  name,
  subject,
  backLabel,
  parentLogoutTone = "orange",
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useSelector((state) => state.user);
  const basePath = getClassBookingBasePath(location.pathname);
  const isParentFlow = basePath.startsWith("/parent/");
  const isVioletTone = isParentFlow && parentLogoutTone === "violet";
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const parentLogoutClassName =
    parentLogoutTone === "violet"
      ? "rounded-full border border-[#d8cfbf] bg-white px-6 py-3 text-sm font-bold text-[#0B2B66] shadow-md transition hover:border-[#08059B] hover:text-[#08059B] sm:text-base"
      : "rounded-full border border-[#d8cfbf] bg-white px-6 py-3 text-sm font-bold text-[#f97316] shadow-md transition hover:border-[#f97316] hover:text-[#f97316] sm:text-base";
  
  const hasProps = heading || subheading || name || subject;
  
  const handleMyBookings = () => {
    navigate(`${basePath}/myBookings`);
  };

  const handleLogout = () => {
    setShowLogoutDialog(true);
  };

  const handleLogoutConfirm = () => {
    setShowLogoutDialog(false);
    dispatch(logout());
    navigate("/tutorinfo", { replace: true });
  };

  return (
    <>
      <header
        className={`w-full px-8 py-4 fixed top-0 left-0 z-50 ${
          isParentFlow ? "bg-[#f8f3ea]/95 backdrop-blur-md" : "bg-transparent"
        }`}
      >
        <div className="w-full flex items-center justify-between">
          <button
            type="button"
            className={`flex items-center gap-3 ${isParentFlow ? "group" : ""}`}
            onClick={() => navigate(-1)}
          >
            {isParentFlow ? (
              <span className="flex h-12 w-12 items-center justify-center rounded-full border border-[#d8cfbf] bg-white text-[#0B2B66] shadow-md transition group-hover:border-[#08059B] group-hover:text-[#08059B]">
                <ArrowLeft size={22} />
              </span>
            ) : (
              <img
                src={GLOBAL_IMAGES.LoginBackButton}
                className="w-14 h-14 cursor-pointer"
                alt="back"
              />
            )}
            {backLabel && (
              <span
                className={`text-lg font-medium ${
                  isParentFlow
                    ? "font-bold text-[#0B2B66] transition group-hover:text-[#08059B]"
                    : "text-gray-900"
                }`}
              >
                {backLabel}
              </span>
            )}
          </button>

          {hasProps && (
            <div className="text-center">
              <h1
                className={`text-3xl font-bold font-mali ${
                  isVioletTone
                    ? "text-[#0B2B66]"
                    : isParentFlow
                      ? "text-[#f97316]"
                      : ""
                }`}
              >
                {heading || "Welcome"}
                {name ? `, ${name}!` : "!"}
              </h1>
              <p
                className={`text-lg -mt-1 font-mali ${
                  isVioletTone
                    ? "text-[#0B2B66]"
                    : isParentFlow
                      ? "text-[#475569]"
                      : ""
                }`}
              >
                {subheading || "Let's help you excel"}
                {subject ? ` in ${subject}` : ""} today
              </p>
            </div>
          )}

          <div className="flex items-center gap-3">
            {hasProps && (
              <button
                onClick={handleMyBookings}
                className={`px-6 py-3 rounded-full shadow-md font-semibold ${
                  isVioletTone
                    ? "bg-[#0B2B66] text-white hover:bg-[#08059B]"
                    : isParentFlow
                      ? "bg-[#f97316] text-white"
                    : "bg-white text-gray-900"
                }`}
              >
                My Bookings
              </button>
            )}
            {isParentFlow && token ? (
              <button
                type="button"
                onClick={handleLogout}
                className={parentLogoutClassName}
              >
                Logout
              </button>
            ) : null}
          </div>
        </div>
      </header>
      <LogoutConfirmDialog
        open={showLogoutDialog}
        onCancel={() => setShowLogoutDialog(false)}
        onConfirm={handleLogoutConfirm}
        tone={isVioletTone ? "violet" : "coral"}
      />
    </>
  );
};

export default Header;
